import {URLS} from './url';
import {
  backendRequest as applicationBackendRequest,
  backendRequestCompleted as applicationBackendRequestCompleted,
  createFolder,
  renameFolder as renameFolderAction,
  renameFolderOk,
  backendRequestReset
} from '../actions/application';
import { updateOrderFolderImap } from '../services/accounts';
import {unicodeUrlAtob as atob, unicodeUrlBtoa as btoa} from './base64';
import {
  backendRequest,
  setFolders,
  updateFolder,
  setShowMoveFolderAlert,
  setIsUpdatedFolder
} from '../actions/folders';
import {deleteMessageCache, renameMessageCache} from './indexed-db';
import {abortControllerWrappers, abortFetch, apiFetch, credentialsHeaders, refreshCredentials, toJson} from './fetch';
import {notifyNewMail} from './notification';
import {renameCache} from '../actions/messages';
import {toApiProxyUrl} from '../utils/utils';

const AUTHENTICATION_FAILED = `199 - "[AUTHENTICATIONFAILED] Invalid credentials (Failure)"`;

export const FolderTypes = Object.freeze({
  INBOX: {serverName: 'INBOX', icon: 'move_to_inbox', position: 0},
  DRAFTS: {attribute: '\\Drafts', icon: 'border_color', position: 1},
  SENT: {attribute: '\\Sent', icon: 'send', position: 2},
  TRASH: {attribute: '\\Trash', icon: 'delete_outline', position: 3},
  FOLDER: {icon: 'folder_open'}
});

export const FolderNames = ['SENT', 'SENT ITEMS', 'ENVIADO', 'ENVIADOS', 'ELEMENTOS ENVIADOS'];


/**
 * Finds the trash folder from within the current folder state's exploded items.
 *
 * TODO: Create cached selector within REDUX state
 * @param foldersState
 * @returns {any}
 */
export const findTrashFolder = foldersState =>
  Object.values(foldersState.explodedItems).find(f => f.type === FolderTypes.TRASH);


export const findSentFolder = foldersState =>
  Object.values(foldersState.explodedItems).find(f => f.type === FolderTypes.SENT);

export const findSentFolderByName = foldersState =>
  Object.values(foldersState.explodedItems).find(f => f.fullName.toUpperCase() === 'SENT' ||
    f.fullName.toUpperCase() === 'SENT ITEMS' ||
    f.fullName.toUpperCase() === 'ENVIADOS' ||
    f.fullName.toUpperCase() === 'ELEMENTOS ENVIADOS' ||
    f.fullName.toUpperCase() === 'INBOX.ENVIADOS');

const SENT_FOLDER_NAMES = ['SENT', 'SENT ITEMS', 'ENVIADOS', 'ELEMENTOS ENVIADOS', 'INBOX.ENVIADOS'];

/**
 * Whether a single message's attached folder (not the whole folders tree —
 * see findSentFolder/findSentFolderByName for that) is the Sent folder.
 * Prefers the IMAP \Sent attribute (locale/rename-proof); falls back to
 * known folder names only when the attribute isn't available.
 *
 * @param folder
 * @returns {boolean}
 */
export const isSentFolder = folder => {
  if (!folder) {
    return false;
  }
  if (folder.type && folder.type.attribute) {
    return folder.type.attribute === FolderTypes.SENT.attribute;
  }
  return typeof folder.fullName === 'string' &&
    SENT_FOLDER_NAMES.includes(folder.fullName.toUpperCase());
};

/**
 * Returns the id of the provided folder and all its children.
 *
 * @param {object} folder
 * @returns {Array.<string>}
 */
export function gatherFolderIds(folder) {
  const gatheredIds = [];
  const gatherFolderIdsInner = folderInner => {
    gatheredIds.push(folderInner.folderId);
    if (Array.isArray(folderInner.children)) {
      folderInner.children.forEach(gatherFolderIdsInner);
    }
    return gatheredIds;
  };
  return gatherFolderIdsInner(folder);
}

/**
 * Renames message cache for a folder and all its children in Redux store and IndexedDB.
 *
 * This step is necessary as the "key" for the folder's message cache is the folder Id which is
 * a base64 encoded string of the folder URL (which includes the name).
 *
 * @param dispatch
 * @param user
 * @param renamedParentFolder
 * @private
 */
function _renameMessageCache(dispatch, user, renamedParentFolder) {
  if (!renamedParentFolder || !renamedParentFolder.previousFolderId) {
    return;
  }
  const decodedPreviousFolderId = atob(renamedParentFolder.previousFolderId);
  const decodedNewFolderId = atob(renamedParentFolder.folderId);
  // Rename all child folders
  gatherFolderIds(renamedParentFolder)
    .forEach(childFolderId => {
      const decodedChildFolderId = atob(childFolderId);
      const decodedPreviousChildFolderId = decodedChildFolderId.replace(decodedNewFolderId, decodedPreviousFolderId);
      const previousChildFolderId = btoa(decodedPreviousChildFolderId);
      // Rename cache in Redux store
      dispatch(renameCache(previousChildFolderId, childFolderId));
      // Rename cache in IndexedDB database
      renameMessageCache(user.id, user.hash, previousChildFolderId, childFolderId);
    });
}

/**
 * Processes an initial folder list and adds the corresponding {@link FolderTypes}
 *
 * @param initialFolders {Array}
 * @returns {Array}
 */
export function processFolders(initialFolders) {
  if (!initialFolders) {
    return null;
  }
  const folders = [];
  const specialFolders = [];
  initialFolders.map(folder => {
    if (folder.name && folder.name.toUpperCase() === FolderTypes.INBOX.serverName) {
      folder.name = 'Bandeja de entrada';
      folder.type = FolderTypes.INBOX;
      specialFolders[folder.type.position] = folder;
    } else if (folder.name) {
      switch (folder.name.toUpperCase()) {
        case 'DRAFTS':
          folder.name = 'Borradores';
          break;
        case 'TRASH':
          folder.name = 'Papelera';
          break;
        case 'SENT':
          folder.name = 'Enviados';
          break;
        case 'DELETED':
          folder.name = 'Eliminados';
          break;
        case 'ARCHIVE':
          folder.name = 'Archivados';
          break;
        case 'JUNK':
          folder.name = 'Correo no deseado';
          break;
        case 'NOTES':
          folder.name = 'Notas';
          break;
        case 'OUTBOX':
          folder.name = 'Bandeja de salida';
          break;
        default:
          break;
      }
      folder.type = FolderTypes.FOLDER;
      // Identify special folder
      let special = false;
      for (const t of [FolderTypes.DRAFTS, FolderTypes.SENT, FolderTypes.TRASH]) {
        if (folder.attributes && folder.attributes.includes(t.attribute)) {
          folder.type = t;
          specialFolders[folder.type.position] = folder;
          special = true;
          break;
        } else if (folder.name && folder.name.toUpperCase() === "PAPELERA") {
          folder.type = FolderTypes.TRASH;
        } else if (folder.name && folder.name.toUpperCase() === "ENVIADOS") {
          folder.type = FolderTypes.SENT;
        }
      }
      // If regular folder just add to the folder array
      if (!special) {
        folders.push(folder);
      }
    }
    folder.children = processFolders(folder.children);
  });
  // Insert special folders in specific positions
  specialFolders.reverse().forEach(f => folders.unshift(f));
  return folders;
}

/**
 * Returns an array with a folder tree with just folderId and children properties.
 *
 * @param {Array} folderTree
 * @returns {{folderId, children: *}[]}
 */
export function removeAttributesFromFolders(folderTree) {
  return [...folderTree].map(({folderId, children}) => ({
    folderId, children: removeAttributesFromFolders(children)
  }));
}

/**
 * Builds an object containing all of the folders and their children at the same level from the provided folderTree
 */
export function explodeFolders(folderTree, explodedFolders = {}) {
  folderTree.forEach(f => {
    explodedFolders[f.folderId] = {...f};
    if (f.children && f.children.length > 0) {
      explodeFolders(f.children, explodedFolders);
      explodedFolders[f.folderId].children = removeAttributesFromFolders(explodedFolders[f.folderId].children);
    }
  });
  return explodedFolders;
}

/**
 * Returns the setFolders action with the preprocessed folders in the payload.
 *
 * @param dispatch
 * @param credentials
 * @param loadChildren
 * @param silent when true (e.g. background poll refresh), skips the global
 *   activeRequests spinner so the UI doesn't flash on every poll tick.
 * @returns {Promise<*>}
 */
export async function getFolders(dispatch, credentials, loadChildren = true, silent = false) {
  try {
    abortFetch(abortControllerWrappers.getFoldersAbortController);
    abortControllerWrappers.getFoldersAbortController = new AbortController();
    const signal = abortControllerWrappers.getFoldersAbortController.signal;
    const url = new URL(URLS.FOLDERS, window.location.origin);

    if (loadChildren) {
      url.search = new URLSearchParams({loadChildren: true}).toString();
    }
    if (!silent) {
      dispatch(backendRequest());
      dispatch(applicationBackendRequest());
    }

    const response = await apiFetch(url, {
      method: 'GET',
      headers: credentialsHeaders(credentials),
      signal: signal
    });

    let existAuthentication = false;

    response.headers.forEach(x => {
      if (AUTHENTICATION_FAILED === x) {
        existAuthentication = true;
      }
    });

    if (existAuthentication) {
      return {unAuthorized: existAuthentication};
    }

    await refreshCredentials(dispatch, response);
    const folders = await toJson(response);
    const foldersAction = setFolders(folders);
    const inbox = Object.values(foldersAction.payload).find(f => f.type === FolderTypes.INBOX);
    if (inbox && inbox.newMessageCount > 0) {
      notifyNewMail();
    }
    return dispatch(foldersAction);
  } catch (exception) {
    if (exception.message === 'Unauthorized') {
      return false;
    }
  } finally {
    if (!silent) {
      dispatch(applicationBackendRequestCompleted());
    }
  }
}

export function renameFolder(
  dispatch,
  user,
  folderToRename,
  newName,
  newUserFolders,
  lexon,
  email
) {
  abortFetch(abortControllerWrappers.getFoldersAbortController);
  dispatch(applicationBackendRequest());

  return apiFetch(toApiProxyUrl(folderToRename._links.rename.href), {
    method: 'PUT',
    headers: credentialsHeaders(user.credentials, { 'Content-Type': 'application/json' }),
    body: newName
  })
    .then(toJson)
    .then(async renamedFolderParent => {
      const setFolderRename = () => {
        if (renamedFolderParent.fullName.length === 0) {
        // Root folder (replace current folder tree)
          dispatch(setFolders(renamedFolderParent.children));
        } else {
          dispatch(setIsUpdatedFolder(true));
          dispatch(updateFolder(renamedFolderParent));
        }

        renamedFolderParent.children
          .filter(f => f.previousFolderId)
          .forEach(f => {
            _renameMessageCache(dispatch, user, f);
            dispatch(renameFolderOk(f.previousFolderId, f.folderId));
          });
        dispatch(renameFolderAction(null));
      };

      const findFolderParent = renamedFolderParent.children.find(f => f.previousFolderId);

      const updatedUserFolder = newUserFolders.map(uf => {
        if (uf.name === findFolderParent.previousFolderId) {
          return {...uf, name: findFolderParent.folderId};
        }
        return uf;
      });

      try {
        const resultUpdated = await updateOrderFolderImap(updatedUserFolder, lexon.userId, email, lexon.lexonToken);
        if (resultUpdated) {
          setFolderRename();
        } else {
          setFolderRename();
        }
      } catch (error) {
        console.error('An error occurred during the updateOrderFolderImap request:', error);
        setFolderRename();
      }

      return { success: true, data: renamedFolderParent };
    })
    .catch(error => {
      console.error(error);
      return { success: false, message: 'El nombre de la carpeta ya existe, prueba con otro.' };
    })
    .finally(() => {
      dispatch(applicationBackendRequestCompleted());
    });
}

/**
 * Triggers BE API to move the provided folderToMove as a child of the specified targetFolder.
 *
 * REDUX store will only be updated once an OK response is received from te server.
 *
 * @param dispatch
 * @param user
 * @param folderToMove
 * @param targetFolder
 */
export function moveFolder(dispatch, user, folderToMove, targetFolder) {
  abortFetch(abortControllerWrappers.getFoldersAbortController);
  dispatch(applicationBackendRequest());
  const targetFolderId = targetFolder ? targetFolder.folderId : null;
  apiFetch(folderToMove._links.move.href, {
    method: 'PUT',
    headers: credentialsHeaders(user.credentials, {'Content-Type': 'application/json'}),
    body: targetFolderId
  })
    .then(async response => {
      const responseBody = await response.text();
      if (response.status === 400) {
        if (responseBody === 'ErrorMoveFolder') {
          dispatch(setShowMoveFolderAlert(true));
        }
        throw new Error('Bad Request');
      }

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          throw new Error(response.statusText);
        } else {
          throw Error(`${response.status}`);
        }
      }

      const jsonResponse = JSON.parse(responseBody);
      return jsonResponse;
    })
    .then(updatedTargetFolder => {
      if (targetFolder) {
        getFolders(dispatch, user.credentials, true);
      } else {
        getFolders(dispatch, user.credentials, true);
      }
      updatedTargetFolder.children
        .filter(f => f.previousFolderId)
        .forEach(f => {
          _renameMessageCache(dispatch, user, f);
          dispatch(renameFolderOk(f.previousFolderId, f.folderId));
        });
    })
    .catch(error => {
      console.error('An error occurred during the moveFolder request:', error);
    })
    .finally(() => {
      dispatch(applicationBackendRequestCompleted());
    });
}

/**
 * Triggers BE API to premanently delete the provided folderToDelete and its children.
 *
 * REDUX store will only be updated once an OK response is received from te server.
 *
 * @param dispatch
 * @param user
 * @param folderToDelete
 */
export function deleteFolder(dispatch, user, folderToDelete) {
  abortFetch(abortControllerWrappers.getFoldersAbortController);
  dispatch(applicationBackendRequest());
  apiFetch(folderToDelete._links.delete.href, {
    method: 'DELETE',
    headers: credentialsHeaders(user.credentials)
  })
    .then(toJson)
    .then(updatedParentFolder => {
      dispatch(updateFolder(updatedParentFolder));
      const folderIds = gatherFolderIds(folderToDelete);
      deleteMessageCache(user.id, user.hash, folderIds);
      getFolders(dispatch, user.credentials, true);
    })
    .catch(error => {
      console.error('An error occurred during the deleteFolder request:', error);
    })
    .finally(() => {
      dispatch(applicationBackendRequestCompleted());
    });
}

/**
 * Creates a new 1st/root level folder.
 *
 * @param dispatch {Dispatch & function}
 * @param credentials {Credentials}
 * @param newFolderName name for the folder to be created
 */
export function createRootFolder(dispatch, user, newFolderName) {
  abortFetch(abortControllerWrappers.getFoldersAbortController);
  dispatch(applicationBackendRequest());
  apiFetch(URLS.FOLDERS, {
    method: 'POST',
    headers: credentialsHeaders(user.credentials),
    body: newFolderName
  })
    .then(toJson)
    .then(folders => {
      dispatch(setFolders(folders));
      dispatch(createFolder(null));
    })
    .catch(error => {
      console.error('An error occurred during the createRootFolder request:', error);
    })
    .finally(() => {
      dispatch(applicationBackendRequestCompleted());
    });
}


/**
 * Creates a new folder under the specified parentFolder.
 *
 * @param dispatch {Dispatch & function}
 * @param credentials {Credentials}
 * @param parentFolder {object} folder to add new child folder
 * @param newFolderName name for the folder to be created
 */
export function createChildFolder(dispatch, user, parentFolder, newFolderName) {
  abortFetch(abortControllerWrappers.getFoldersAbortController);
  dispatch(applicationBackendRequest());
  apiFetch(parentFolder._links.self.href, {
    method: 'POST',
    headers: credentialsHeaders(user.credentials),
    body: newFolderName
  })
    .then(toJson)
    .then(updatedParentFolder => {
      dispatch(updateFolder(updatedParentFolder));
      dispatch(setIsUpdatedFolder(true));
      dispatch(createFolder(null));
      dispatch(applicationBackendRequestCompleted());
    })
    .catch(error => {
      console.error('An error occurred during the createChildFolder request:', error);
    })
    .finally(() => {
      dispatch(applicationBackendRequestCompleted());
    });
}

export async function getMailBoxUsage(dispatch, credentials) {
  try {
    const url = new URL(`${URLS.FOLDERS}/inbox/storage-usage`, window.location.origin);

    dispatch(applicationBackendRequest());
    const response = await apiFetch(url, {
      method: 'GET',
      headers: credentialsHeaders(credentials),
    });

    const mailBoxUsage = await toJson(response);
    
    return mailBoxUsage;
  } catch (error) {
    console.error('An error occurred during the getMailBoxUsage request:', error);
  } finally {
    dispatch(applicationBackendRequestCompleted());
  }
}

export function handleRackendRequestReset(dispatch) {
  dispatch(backendRequestReset());
}
